import { ofType } from 'redux-observable';
import { combineLatest, Observable, of, Subscriber } from 'rxjs';
import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { Market } from '@bridebook/toolbox/src/gazetteer';
import { Action } from '@reduxjs/toolkit';
import { AuthActionTypes, UserSetupCompletedAction } from '@bridebook/web/lib/auth/action-types';
import { orderSupplierTypesByBookingPriority } from '@bridebook/web/lib/search-landing/utils';
import { updateSearchCategory } from '@bridebook/web/lib/search/actions';
import { isSearchResultsPage } from '@bridebook/web/lib/search/utils';
import {
  FetchUserShortlistSuccessAction,
  ShortlistActionTypes,
} from '@bridebook/web/lib/shortlist/action-types';
import { getBookedSupplierCategories } from '@bridebook/web/lib/shortlist/selectors';
import { getListOfRemainingSuppliers } from '@bridebook/web/lib/supplier/utils/get-list-of-remaining-suppliers';
import type { IApplicationState, IEpicDeps, TWeddingSerialized } from '@bridebook/web/lib/types';
import { withMarket } from '@bridebook/web/lib/utils/operators/with-market';
import { IUpdateWeddingAction, WeddingActionTypes } from '@bridebook/web/lib/weddings/action-types';
import { isBrideOnlyWedding, isGroomOnlyWedding } from '@bridebook/web/lib/weddings/selectors';

type ObserverNextType = ReturnType<typeof updateSearchCategory>;

const nextSupplierCategory = (
  state: IApplicationState,
  observer: Subscriber<ObserverNextType>,
  market: Market,
  wedding?: TWeddingSerialized,
) => {
  if (wedding) {
    const supplierTypesByBookingPriority = orderSupplierTypesByBookingPriority(market.suppliers);
    const booked = getBookedSupplierCategories(state) ?? [];
    const isGroomOnly = isGroomOnlyWedding(state);
    const isBrideOnly = isBrideOnlyWedding(state);
    const prevCategory = state.search.request.slug;
    const remainingSuppliers = getListOfRemainingSuppliers(
      supplierTypesByBookingPriority,
      booked,
      isGroomOnly,
      isBrideOnly,
    );

    const nextCategory = remainingSuppliers[0] || 'venue';
    if (nextCategory !== prevCategory) {
      observer.next(updateSearchCategory(nextCategory, isSearchResultsPage(state.app.pathname)));
    }
  }
};

export const updateSearchbarCategoryOnProfileChangeEpic = (
  action$: Observable<Action>,
  { state$ }: IEpicDeps,
): Observable<Action> => {
  const shortlistSuccess$ = action$.pipe<FetchUserShortlistSuccessAction>(
    ofType(ShortlistActionTypes.FETCH_USER_SHORTLIST_SUCCESS),
  );
  const weddingUpdate$ = action$.pipe<IUpdateWeddingAction>(
    ofType(WeddingActionTypes.UPDATE_WEDDING),
  );
  const userSetupCompleted$ = action$.pipe<UserSetupCompletedAction>(
    ofType(AuthActionTypes.USER_SETUP_COMPLETED),
  );

  return combineLatest([shortlistSuccess$, weddingUpdate$, userSetupCompleted$]).pipe(
    withLatestFrom(state$),
    mergeMap(([[_, weddingUpdateAction, userSetupCompletedAction]]) =>
      withMarket(state$)(of(weddingUpdateAction || userSetupCompletedAction)).pipe(
        mergeMap(({ state, market }) => {
          const actionPayload = (weddingUpdateAction || userSetupCompletedAction).payload;
          const { wedding } = actionPayload;
          return new Observable<Action>((observer) => {
            nextSupplierCategory(state, observer, market, wedding);
            observer.complete();
          });
        }),
      ),
    ),
  );
};
